import {
  useQuery as useBaseQuery,
  type UseQueryResult as UseBaseQueryResult,
} from "@tanstack/react-query";

export type UseQueryOptions<D = unknown, E = unknown> = {
  cacheTime?: number;
  enabled?: boolean;
  initialData?: D;
  keepPreviousData?: boolean;
  refetchInterval?: number | false | ((data: D | undefined) => number | false);
  refetchIntervalInBackground?: boolean;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
  retry?: boolean | number | ((failureCount: number, error: E) => boolean);
  retryDelay?: number | ((failureCount: number) => number);
  staleTime?: number;
  useErrorBoundary?: boolean;
  suspense?: boolean;
};

export type UseQueryResult<D = unknown, E = unknown> = (
  | {
      data: undefined;
      error: E;
      isError: true;
      isIdle: false;
      isLoading: false;
      isSuccess: false;
      status: "error";
    }
  | {
      data: undefined;
      error: null;
      isError: false;
      isIdle: true;
      isLoading: false;
      isSuccess: false;
      status: "idle";
    }
  | {
      data: undefined;
      error: null;
      isError: false;
      isIdle: false;
      isLoading: true;
      isSuccess: false;
      status: "loading";
    }
  | {
      data: D;
      error: null;
      isError: false;
      isIdle: false;
      isLoading: false;
      isSuccess: true;
      status: "success";
    }
) & {
  isFetching: boolean;
  refetch: () => Promise<UseQueryResult<D, E>>;
};

const refetchMap = new WeakMap();

const toUseQueryResult = <D, E>({
  data,
  error,
  status,
  fetchStatus,
  isFetching,
  refetch,
}: UseBaseQueryResult<D, E>): UseQueryResult<D, E> => {
  const rest = {
    isFetching,
    refetch:
      refetchMap.get(refetch) ??
      refetchMap
        .set(refetch, () => refetch().then(toUseQueryResult))
        .get(refetch),
  };

  switch (status) {
    case "error": {
      return {
        data: undefined,
        error,
        isError: true,
        isIdle: false,
        isLoading: false,
        isSuccess: false,
        status: "error",
        ...rest,
      };
    }
    case "loading": {
      return fetchStatus === "idle"
        ? {
            data: undefined,
            error: null,
            isError: false,
            isIdle: true,
            isLoading: false,
            isSuccess: false,
            status: "idle",
            ...rest,
          }
        : {
            data: undefined,
            error: null,
            isError: false,
            isIdle: false,
            isLoading: true,
            isSuccess: false,
            status: "loading",
            ...rest,
          };
    }
    case "success": {
      return {
        data: data,
        error: null,
        isError: false,
        isIdle: false,
        isLoading: false,
        isSuccess: true,
        status: "success",
        ...rest,
      };
    }
  }
};

export type UseSuspenseQueryOptions<D = unknown, E = unknown> = Omit<
  UseQueryOptions<D, E>,
  "suspense"
>;

/**
 * @deprecated Use `useQuery` from `createApi` instead.
 */
export const useSuspenseQuery = <D extends {}, E>(
  key: readonly unknown[],
  fn: () => D | Promise<D>,
  options?: UseSuspenseQueryOptions<D, E>,
): UseQueryResult<D, E> => {
  return toUseQueryResult(
    useBaseQuery<D, E>(key, fn, {
      ...options,
      suspense: true,
      networkMode: "always",
    }),
  );
};

export type UseOnDemandQueryOptions<D = unknown, E = unknown> = Omit<
  UseQueryOptions<D, E>,
  "suspense"
>;

/**
 * @deprecated Use `useOnDemandQuery` from `createApi` instead.
 */
export const useOnDemandQuery = <D extends {}, E>(
  key: readonly unknown[],
  fn: () => D | Promise<D>,
  options?: UseOnDemandQueryOptions<D, E>,
): UseQueryResult<D, E> => {
  return toUseQueryResult(
    useBaseQuery<D, E>(key, fn, {
      ...options,
      suspense: false,
      networkMode: "always",
    }),
  );
};
